import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "../../../components/Button";
import { Flex } from "../../../components/Flex";
import { Input } from "../../../components/Input";
import { Text } from "../../../components/Text";
import { Modal } from "../../../components/modal/Modal";
import { Integration } from "../../../model/integration/Integration";
import { CreateIntegrationDto } from "../../../model/integration/dto/CreateIntegrationDto";
import { UpdateIntegrationDto } from "../../../model/integration/dto/UpdateIntegrationDto";
import { WhatsappIntegrationDto } from "../../../model/integration/dto/WhatsappIntegrationDto";
import { IntegrationKind } from "../../../model/integration/types/IntegrationKind";
import { integrationSliceSelectors } from "../../../redux/integration/integration.selector";
import { integrationActions } from "../../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";

type IntegrationAddModalWhatsappProps = {
  open: boolean;
  onClose: () => void;
};

export const IntegrationAddModalWhatsapp: React.FC<
  IntegrationAddModalWhatsappProps
> = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const existingWhatsappIntegration = useAppSelector(
    integrationSliceSelectors.selectUserIntegrations,
  )?.find(({ kind }) => kind === IntegrationKind.WHATSAPP) as
    | Integration<WhatsappIntegrationDto>
    | undefined;

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { control, getValues, handleSubmit } = useForm<
    Pick<CreateIntegrationDto<WhatsappIntegrationDto>, "data">
  >({
    defaultValues: {
      data: {
        phoneNumber: existingWhatsappIntegration?.config?.phoneNumber || "",
      },
    },
  });

  const onSubmit = async () => {
    try {
      setLoading(true);
      const dto = {
        ...getValues(),
        userId,
      };
      const editMode = Boolean(existingWhatsappIntegration?._id);

      if (editMode) {
        await dispatch(
          integrationActions.updateIntegration(
            dto as UpdateIntegrationDto<WhatsappIntegrationDto>,
          ),
        ).unwrap();
      } else {
        await dispatch(
          integrationActions.createIntegration(
            dto as CreateIntegrationDto<WhatsappIntegrationDto>,
          ),
        ).unwrap();
      }

      await dispatch(
        integrationActions.getUserIntegrations({ userId }),
      ).unwrap();

      if (editMode) {
        AtiraToast.success(t("integration.add-modal.success-update"));
      } else {
        AtiraToast.success(t("integration.add-modal.success"));
      }
      onClose();
    } catch (e: any) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("integration.add-modal.whatsapp.title")}
    >
      <Flex flexDirection="column" gap="m">
        <Flex flexDirection="column" gap="m">
          <Text>{t("integration.add-modal.tooltip.whatsapp")}</Text>
          <Text>{t("integration.add-modal.tooltip.description")}</Text>
        </Flex>

        <Flex flexDirection="column">
          <Controller
            control={control}
            name="data.phoneNumber"
            rules={{
              required: {
                value: true,
                message: t("common.this_field_required"),
              },
            }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Input
                onChange={(e) => {
                  const numericValue = e.target.value.replace(/[^0-9]/g, "");

                  onChange(numericValue);
                }}
                value={value}
                valid={!error?.message}
                errorMessage={error?.message}
                type="tel"
                placeholder={t("integration.add-modal.whatsapp.placeholder")}
              />
            )}
          />

          <Button
            hover
            title={t("common.save")}
            onClick={handleSubmit(onSubmit)}
            loading={loading}
            height="2.3rem"
            marginTop="m"
          />
        </Flex>
      </Flex>
    </Modal>
  );
};
